/**
 * Client-side image compression utilities
 * Compresses images before upload to save bandwidth and storage
 */

export interface CompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number; // 0.0 to 1.0
  maxSizeMB?: number;
}

const DEFAULT_OPTIONS: Required<CompressionOptions> = {
  maxWidth: 1920,
  maxHeight: 1080,
  quality: 0.85,
  maxSizeMB: 2
};

/**
 * Compress a single image file
 */
export async function compressImage(
  file: File,
  options: CompressionOptions = {}
): Promise<File> {
  const opts = { ...DEFAULT_OPTIONS, ...options };

  // If file is already small enough, return as-is
  const fileSizeMB = file.size / 1024 / 1024;
  if (fileSizeMB <= opts.maxSizeMB) {
    return file;
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();

      img.onload = () => {
        try {
          // Calculate new dimensions while maintaining aspect ratio
          let { width, height } = img;

          if (width > opts.maxWidth) {
            height = (height * opts.maxWidth) / width;
            width = opts.maxWidth;
          }

          if (height > opts.maxHeight) {
            width = (width * opts.maxHeight) / height;
            height = opts.maxHeight;
          }

          // Create canvas and draw resized image
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Failed to get canvas context'));
            return;
          }

          // Use better image smoothing
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, width, height);

          // Convert to blob with compression
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('Failed to compress image'));
                return;
              }

              // Create new File from compressed blob
              const compressedFile = new File([blob], file.name, {
                type: file.type || 'image/jpeg',
                lastModified: Date.now()
              });

              resolve(compressedFile);
            },
            file.type || 'image/jpeg',
            opts.quality
          );
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };

      img.src = e.target?.result as string;
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    reader.readAsDataURL(file);
  });
}

/**
 * Compress multiple image files
 */
export async function compressImages(
  files: File[],
  options: CompressionOptions = {}
): Promise<File[]> {
  const compressionPromises = files.map(file => compressImage(file, options));
  return Promise.all(compressionPromises);
}

/**
 * Get compression statistics
 */
export function getCompressionStats(originalFile: File, compressedFile: File) {
  const originalSizeMB = originalFile.size / 1024 / 1024;
  const compressedSizeMB = compressedFile.size / 1024 / 1024;
  const reduction = ((originalSizeMB - compressedSizeMB) / originalSizeMB) * 100;

  return {
    originalSize: originalSizeMB.toFixed(2) + ' MB',
    compressedSize: compressedSizeMB.toFixed(2) + ' MB',
    reduction: reduction.toFixed(1) + '%',
    originalBytes: originalFile.size,
    compressedBytes: compressedFile.size
  };
}
